import { useMemo } from 'react';
import { type IBooleanCondition } from '../../../inputs/expressions-editor/conditions/IBooleanCondition.tsx';
import { useExpressionValue } from '../../expressions/render/hooks/useExpressionValue.tsx';
import { type IWidgetParsedProps } from '../repository/inputs/common/util/getWidgetProps.ts';
import { conditionResultToBoolean } from './conditionResultToBoolean.ts';

export interface IUseBooleanConditionOptions {
  name: IWidgetParsedProps['name'];
  preview?: IWidgetParsedProps['preview'];
  skip?: boolean;
}

/**
 * Hook per la gestione centralizzata delle condizioni booleane dei widget.
 * Permette di gestire la visibilità e l'abilitazione dei widget in base
 * alle condizioni booleane (statiche o dinamiche) impostate.
 */
export function useBooleanCondition(
  condition: IBooleanCondition | null | undefined,
  options: IUseBooleanConditionOptions,
) {
  // Recupera il valore dell'espressione
  // N.B. L'hook contiene già lo useWatch sui campi dipendenti
  const expressionValue = useExpressionValue(
    options.name,
    condition && condition.type === 'expression' ? condition?.expression : null,
  );

  return useMemo(() => {
    if (Boolean(options.skip) || options.preview) return true;

    return conditionResultToBoolean(condition, expressionValue);
  }, [condition, expressionValue, options.preview, options.skip]);
}
